import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AppContext";
import { toast } from "sonner";

export default function Login() {
  const { login, googleLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const u = await login(email, password); nav(u.role === "admin" ? "/admin" : "/account"); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 md:px-8 py-16">
      <h1 className="font-display text-4xl text-center">Welcome to ASURE</h1>
      <p className="text-center text-asure-text2 mt-2 text-sm">Sign in to save your enquiries and track your ASURE journey.</p>

      <div className="mt-10 card-surface p-8">
        <button onClick={googleLogin} data-testid="google-signin"
          className="w-full inline-flex items-center justify-center gap-3 py-3 px-4 rounded-full border border-[color:var(--asure-border)] bg-white hover:bg-asure-ivory transition-colors font-semibold text-sm text-asure-text">
          <GoogleIcon /> Continue with Google
        </button>
        <p className="mt-3 text-[11px] text-center text-asure-text2">Secure sign-in powered by Google · No password needed</p>

        <div className="flex items-center gap-3 my-8">
          <div className="hairline flex-1" />
          <span className="text-[10px] uppercase tracking-widest text-asure-text2">or</span>
          <div className="hairline flex-1" />
        </div>

        {!showAdmin ? (
          <button onClick={() => setShowAdmin(true)} className="w-full text-xs text-asure-text2 hover:text-asure-forest link-underline" data-testid="show-admin">
            Sign in as ASURE Admin
          </button>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="eyebrow text-center">Admin sign-in</div>
            <Field label="Email" type="email" v={email} on={setEmail} tid="login-email" />
            <Field label="Password" type="password" v={password} on={setPassword} tid="login-password" />
            <button className="btn-primary w-full" disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Admin Sign In"}</button>
          </form>
        )}
      </div>
      <div className="mt-6 text-center text-xs text-asure-text2">
        By continuing you agree to be contacted by ASURE regarding your enquiries.
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", tid }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <input required type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="w-full border border-[color:var(--asure-border)] rounded-lg px-4 py-3 focus:border-asure-forest outline-none" />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 35.5 24 35.5c-6.4 0-11.5-5.2-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.5 0 19-7.7 19-19 0-1.4-.1-2.7-.4-4z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.5 2.2-7.1 2.2-5.3 0-9.6-3-11.3-7.5l-6.5 5C9.6 39.1 16.2 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.5v.1l6.1 5c-.4.4 6.7-4.9 6.7-14.6 0-1.4-.1-2.7-.4-4z"/>
    </svg>
  );
}
